import {MemoizedSelector} from '@ngrx/store';
import {LoadStatus, PRELOAD} from './common/common.types';
import {PickStringKeysByTypeFrom} from './utility-type.helpers';

export type MxSelector<T> = MemoizedSelector<object, T>;
export type MxSelectorCreator<P extends unknown[], T> = (...params: P) => MxSelector<T>;

export type MxSelectorOrSimply<T> = MxSelector<T> | T;

/* eslint-disable @typescript-eslint/no-explicit-any -- This is literally "any"! */
export type MxSelectorCreatorBaseObjectType<M> = M extends MxSelectorCreator<[infer BaseObjectType], any> ? BaseObjectType : never;
export type MxSelectorCreatorReturnType<M> = M extends MxSelectorCreator<any, infer ReturnType> ? ReturnType : never
/* eslint-enable @typescript-eslint/no-explicit-any */

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- This literally means a selector creator whose projector returns any type.
export type AnyMxSelectorCreatorMatching<T> = MxSelectorCreator<[T], any>;

export type MxSelectorNameMatching<T, SelectorsContainer extends object> =
  PickStringKeysByTypeFrom<SelectorsContainer, MxSelector<T>>;

export type MxSelectorCreatorNameMatching<T, SelectorsContainer extends object> =
  PickStringKeysByTypeFrom<SelectorsContainer, AnyMxSelectorCreatorMatching<T>>;

export type MxSelectorCreatorNameMatchingWithReturnType<T, SelectorsContainer extends object, ReturnType> =
  PickStringKeysByTypeFrom<SelectorsContainer, MxSelectorCreator<[T], ReturnType>>;

type GeneratedSelectorNameFrom<SelectorName, NamePrefix extends string> =
  NamePrefix extends ''
    ? SelectorName  // No prefix, so we already have the name.
    : SelectorName extends string // Must check this explicitly, or TypeScript won't allow the type conversion on the next line.
      ? `${NamePrefix}${Capitalize<SelectorName>}` // 'prefix' + 'selectorName' => 'prefixSelectorName'
      : never;

export type GeneratedSelectorsFor<ObjectType, NamePrefix extends string> = {
  [SelectorName in keyof ObjectType as GeneratedSelectorNameFrom<SelectorName, NamePrefix>]: MxSelector<ObjectType[SelectorName]>;
};

type GeneratedSelectorCreatorNameFrom<SelectorCreatorName, NamePrefix extends string> =
  NamePrefix extends ''
    ? SelectorCreatorName extends string // Must check this explicitly, or TypeScript won't allow the type conversion on the next line.
      ? `${SelectorCreatorName}For` // No prefix, so just add our suffix to the name.
      : never
    : SelectorCreatorName extends string // Must check this explicitly, or TypeScript won't allow the type conversion on the next line.
      ? `${NamePrefix}${Capitalize<SelectorCreatorName>}For` // 'prefix' + 'selectorName' => 'prefixSelectorNameFor'
      : never;

export type GeneratedSelectorCreatorsFor<ObjectType, NamePrefix extends string, BaseObjectType> = {
  [SelectorName in keyof ObjectType as GeneratedSelectorCreatorNameFrom<SelectorName, NamePrefix>]: // key index
  MxSelectorCreator<[BaseObjectType], ObjectType[SelectorName]>; // value
};

export const NO_NAME_PREFIX = ''; // For use with selector generators and selector creator generators.

export type NumberHelperSelectorType = 'IS_NEGATIVE' | 'IS_ZERO' | 'IS_POSITIVE';
export type NumberHelperProjector = (number: number) => boolean;

export type ArrayHelperSelectorType = 'IS_EMPTY' | 'IS_POPULATED';
export type ArrayHelperProjector = (array: unknown[]) => boolean;

export type LoadStatusSubstate = {
  loadStatus: LoadStatus;
};

export const INITIAL_LOAD_STATUS_SUBSTATE: LoadStatusSubstate = {
  loadStatus: PRELOAD
};

export interface LoadStatusSelectors {
  loadStatus: MxSelector<LoadStatus>;
  preload: MxSelector<boolean>;
  loading: MxSelector<boolean>;
  awaitingFirstLoad: MxSelector<boolean>;
  reloading: MxSelector<boolean>;
  awaitingAnyLoad: MxSelector<boolean>;
  loaded: MxSelector<boolean>;
  loadFailed: MxSelector<boolean>;
  finishedLoading: MxSelector<boolean>;
}

export type MinimalPersonNameSelectorName =
  'lastNameWithSuffix'
  | 'fullName'
  | 'abbreviatedName';

export type MinimalPersonNameSelectors<NamePrefix extends string = ''> = {
  [SelectorName in MinimalPersonNameSelectorName as PersonSelectorName<NamePrefix, SelectorName>]: MxSelector<string>;
};

export type MinimalPersonNameSelectorCreators<ObjectType, NamePrefix extends string = ''> = {
  [SelectorName in MinimalPersonNameSelectorName as PersonSelectorCreatorName<NamePrefix, SelectorName>]: PersonNameCreatorWithFallback<ObjectType>;
};

export type PersonNameSelectorName =
  'preferredOrFirstName'
  | 'lastNameWithSuffix'
  | 'fullName'
  | 'fullPreferredName'
  | 'abbreviatedName'
  | 'abbreviatedPreferredName'
  | 'fullNameWithPronouns'
  | 'fullPreferredNameWithPronouns';

export type PersonNameSelectors<NamePrefix extends string = ''> = {
  [SelectorName in PersonNameSelectorName as PersonSelectorName<NamePrefix, SelectorName>]: MxSelector<string>;
};

export type PersonSelectorName<NamePrefix extends string, SelectorName extends string> =
  OptionallyPrefixed<NamePrefix, SelectorName>;

export type PersonNameSelectorCreators<ObjectType, NamePrefix extends string = ''> = {
  [SelectorName in PersonNameSelectorName as PersonSelectorCreatorName<NamePrefix, SelectorName>]: PersonNameCreatorWithFallback<ObjectType>;
};

export type PersonSelectorCreatorName<NamePrefix extends string, SelectorName extends string> =
  OptionallyPrefixed<NamePrefix, AsSelectorCreatorName<SelectorName>>;

type PersonNameCreatorWithFallback<ObjectType> = (object: ObjectType, fallback?: string) => MxSelector<string>;

type AsSelectorCreatorName<SelectorName extends string> =
  `${SelectorName}For`;

type OptionallyPrefixed<NamePrefix extends string, SelectorName extends string> =
  NamePrefix extends '' ? SelectorName : `${NamePrefix}${Capitalize<SelectorName>}`;
